import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LOCALSTORAGE_KEYS } from 'src/app/constants';
import Button from 'src/app/components/Button/Button';
import styles from './Onboarding.module.scss';
import WelcomeScreenOne from './WelcomeScreenOne/WelcomeScreenOne';
import WelcomeScreenTwo from './WelcomeScreenTwo/WelcomeScreenTwo';
import WelcomeScreenThree from './WelcomeScreenThree/WelcomeScreenThree';
import { t } from 'i18next';

type OnboardingPropType = {
  onProceed: () => void;
};

const NUMBER_OF_WELCOME_SCREENS = 3;
/**
 * Onboarding Screen on Tutor launch for first time users
 * Starts conversation on CTA
 */
function Onboarding(props: OnboardingPropType) {
  const { onProceed } = props;

  const [activeScreenIndex, setActiveScreenIndex] = useState(1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<Array<HTMLSpanElement | null>>([]);

  const onStartSession = useCallback(() => {
    localStorage.setItem(LOCALSTORAGE_KEYS.IS_RETURNING_USER, 'true');
    onProceed();
  }, [onProceed]);

  // Roving tabindex (WAI-ARIA APG tablist pattern): only the active dot is a
  // Tab stop, so AT doesn't read all 3 tabs on encountering the tablist.
  // Arrow keys move focus between dots without changing the active screen —
  // slide progression stays driven by Skip/Next, not the dots themselves.
  const handleDotKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>, screenIndex: number) => {
      let nextIndex: number | null = null;

      if (event.key === 'ArrowRight') {
        nextIndex = (screenIndex + 1) % NUMBER_OF_WELCOME_SCREENS;
      } else if (event.key === 'ArrowLeft') {
        nextIndex = (screenIndex - 1 + NUMBER_OF_WELCOME_SCREENS) % NUMBER_OF_WELCOME_SCREENS;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        dotRefs.current[nextIndex]?.focus();
      }
    },
    []
  );

  // Move focus into the carousel on mount so screen readers announce the
  // welcome content before the user tabs to Skip/Next or reads Exercise ID.
  useEffect(() => {
    wrapperRef.current?.focus();
  }, []);

  // Memoized so unrelated re-renders of Onboarding's ancestors don't recreate
  // these elements unnecessarily.
  const introductionContent = useMemo(() => {
    if (activeScreenIndex === 1) return <WelcomeScreenOne />;
    if (activeScreenIndex === 2) return <WelcomeScreenTwo />;
    return <WelcomeScreenThree onStartSession={onStartSession} />;
  }, [activeScreenIndex, onStartSession]);

  // Without this, clicking Next leaves focus sitting on the (still-mounted)
  // Next button, so AT's reading cursor just continues linearly from there
  // through whatever the next screen's DOM happens to contain — causing
  // skipped/jumbled announcements (tab label missing, Skip/Next re-read,
  // etc). Refocusing the wrapper restarts the read from the top of the
  // carousel every time, same as the initial mount.
  const goToNextScreen = useCallback(() => {
    setActiveScreenIndex((prev) => prev + 1);
    requestAnimationFrame(() => wrapperRef.current?.focus());
  }, []);

  return (
    <div tabIndex={0} ref={wrapperRef} className={styles.onboardingWrapper}>
      <div className={styles.onboardingCard}>
        <div className={styles.introduction}>{introductionContent}</div>

        {activeScreenIndex < NUMBER_OF_WELCOME_SCREENS && (
          <div className={styles.actions}>
            <Button
              buttonText={t('SKIP')}
              className={`${styles.skipButton} !bg-transparent text-main`}
              size='small'
              onClickHandler={onStartSession}
            />
            <Button
              buttonText={t('NEXT')}
              className={styles.nextButton}
              size='small'
              onClickHandler={goToNextScreen}
            />
          </div>
        )}

        {/* No role="tablist" here: with only the active dot exposed to AT
            (others aria-hidden), a tablist parent makes screen readers
            compute and announce a group position (e.g. "1 of 1") on top of
            our own "N of 3" label — redundant and confusing. */}
        <div className={styles.progressDots}>
          {[...Array(NUMBER_OF_WELCOME_SCREENS)].map((_, screenIndex) => {
            const isActive = screenIndex + 1 === activeScreenIndex;
            return (
              <span
                key={screenIndex}
                ref={(el) => (dotRefs.current[screenIndex] = el)}
                tabIndex={isActive ? 0 : -1}
                role='tab'
                aria-selected={isActive}
                aria-hidden={!isActive}
                aria-label={t('SLIDE_OF', {
                  current: screenIndex + 1,
                  total: NUMBER_OF_WELCOME_SCREENS,
                })}
                onKeyDown={(event) => handleDotKeyDown(event, screenIndex)}
                className={`${styles.progressDot} ${isActive ? styles.progressDotActive : ''}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
